import { type PayloadAction } from "@reduxjs/toolkit";
import type { currentSurveySliceState } from "@survey-types/survey.core.types";
import type { MapKey } from "@survey-types/survey.mapkeys.types";

type RawMapKey = MapKey & {
  category?: string;
  referenceID?: string;
};

const normalizeCategory = (
  category?: string,
): MapKey["category"] | undefined => {
  const normalized = category?.toUpperCase().replace(/[^A-Z]/g, "");
  if (normalized?.startsWith("POI") || normalized === "POINTOFINTEREST") return "POI";
  if (normalized === "PROPERTY") return "Property";
  if (normalized === "LEASECOMP") return "LeaseComp";
  return category as MapKey["category"] | undefined;
};

const normalizeMapKey = (raw: RawMapKey): MapKey => ({
  ...raw,
  active: raw.active ?? true,
  category: normalizeCategory(raw.category),
  referenceId: raw.referenceId ?? raw.referenceID,
});

// =====================================
// MAP KEY STATE MANAGEMENT
// =====================================

export const mapKeyReducers = {
  setMapKeys: (
    state: currentSurveySliceState,
    action: PayloadAction<MapKey[]>,
  ) => {
    state.marketMapKeys = action.payload.map((item) =>
      normalizeMapKey(item as RawMapKey),
    );
  },

  addMapKeys: (
    state: currentSurveySliceState,
    action: PayloadAction<MapKey>,
  ) => {
    state.marketMapKeys.push(normalizeMapKey(action.payload as RawMapKey));
  },

  deleteMapKey: (
    state: currentSurveySliceState,
    action: PayloadAction<string>,
  ) => {
    state.marketMapKeys = state.marketMapKeys.filter(
      (item) => item.mapKeyId !== action.payload,
    );
  },

  updateToggleLabelMapKey: (
    state: currentSurveySliceState,
    action: PayloadAction<MapKey>,
  ) => {
    const index = state.marketMapKeys.findIndex(
      (item) => item.mapKeyId === action.payload.mapKeyId,
    );
    if (index !== -1) {
      state.marketMapKeys[index] = normalizeMapKey(action.payload as RawMapKey);
    }
  },

  reorderMapKeys: (
    state: currentSurveySliceState,
    action: PayloadAction<{ oldIndex: number; newIndex: number }>,
  ) => {
    const { oldIndex, newIndex } = action.payload;
    const [movedItem] = state.marketMapKeys.splice(oldIndex, 1);
    state.marketMapKeys.splice(newIndex, 0, movedItem);
  },
};
